/*
 * Created on 31.03.2016
 *
 * Copyright(c) 1995 - 2016 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

/**
 * Diese Utilklasse stellt verschiedene Methoden bereit um HttpHeader zu manipulieren ohne
 * diese global zu ändern. Dabei werden die globalen Header übernommen.
 * Diese Klasse ist nötig, da in Angular2 Header nicht Requestspezifisch gemerged werden
 * können: https://github.com/angular/angular/issues/6060
 *
 * @author Valentin Kennke (vake@mms-dresden.de)(valentin.kennke@t-systems.com)
 */
@Injectable()
export class HttpHeaderUtil {


    /**
     * Fügt einen Header hinzu. Bereits bestehende Header mit gleichem Namen werde nicht überschrieben.
     *
     * @param name der Name des Headers
     * @param value der Wert des Headers
     * @param headers Header die standardmäßig hinzugefügt werden. Per default sind das die global gesetzten Header
     * @returns {Headers} das erweiterte Headers-Objekt
     */
    appendForRequest(name: string, value: string, headers?: HttpHeaders) {
        let newHeaders = new HttpHeaders();
        if (headers) {
            headers.keys().forEach(key => newHeaders = newHeaders.append(key, headers.get(key)));
        }
        newHeaders = newHeaders.append(name, value);
        return newHeaders;
    }

    /**
     * Fügt mehrere Header hinzu, d.h. die übergebenen Header werden gemerged.
     *
     * @param headersToAppend die Header die hinzugefügt werden sollen
     * @param headers Header die standardmäßig hinzugefügt werden. Per default sind das die global gesetzten Header
     * @returns {Headers} das erweiterte Headers-Objekt
     */
    appendMultipleForRequest(headersToAppend: HttpHeaders, headers?: HttpHeaders) {
        let newHeaders = new HttpHeaders();
        if (headers) {
            headers.keys().forEach(key => newHeaders = newHeaders.append(key, headers.get(key)));
        }
        headersToAppend.keys().forEach(key => newHeaders = newHeaders.append(key, headersToAppend.get(key)));
        return newHeaders;
    }

    /**
     * Entfernt einen Header.
     *
     * @param name der Name des zu entferndenden Headers
     * @param headers Header die standardmäßig hinzugefügt werden. Per default sind das die global gesetzten Header
     * @returns {Headers} das geänderte Headers-Objekt
     */
    deleteForRequest(name: string, headers?: HttpHeaders) {
        let newHeaders = new HttpHeaders();
        if (headers) {
            headers.keys().forEach(key => newHeaders = newHeaders.append(key, headers.get(key)));
        }
        newHeaders = newHeaders.delete(name);
        if (name === 'Content-Type') {
            newHeaders = newHeaders.append('Content-Type', '');
        }
        if (name === 'Accept') {
            newHeaders = newHeaders.append('Accept', '');
        }
        return newHeaders;
    }

    /**
     * Entfernt mehrere Header.
     *
     * @param headersToDelete Header die entfernt werden sollen.
     * @param headers Header die standardmäßig hinzugefügt werden. Per default sind das die global gesetzten Header
     * @returns {Headers} das geänderte Headers-Objekt
     */
    deleteMultipleForRequest(headersToDelete: HttpHeaders, headers?: HttpHeaders) {
        let newHeaders = new HttpHeaders();
        if (headers) {
            headers.keys().forEach(key => newHeaders = newHeaders.append(key, headers.get(key)));
        }
        headersToDelete.keys().forEach(key => newHeaders = newHeaders.delete(key));
        return newHeaders;
    }

    /**
     * Setzt einen Header. Im Gegensatz zu appendForRequest werden bereits vorhandene Header mit gleichem Namen
     * überschrieben.
     *
     * @param name der Name des Headers
     * @param value der Wert des Headers
     * @param headers Header die standardmäßig hinzugefügt werden. Per default sind das die global gesetzten Header
     * @returns {Headers} das geänderte Headers-Objekt
     */
    setForRequest(name: string, value: string, headers?: HttpHeaders) {
        let newHeaders = new HttpHeaders();
        if (headers) {
            headers.keys().forEach(key => newHeaders = newHeaders.append(key, headers.get(key)));
        }
        newHeaders = newHeaders.set(name, value);
        return newHeaders;
    }

    /**
     * Setzt mehrere Header. Im Gegensatz zu appendMultipleForRequest werden bereits vorhandene Header mit gleichem
     * Namen überschrieben.
     *
     * @param headersToAppend Header die gesetzt werden sollen
     * @param headers Header die standardmäßig hinzugefügt werden. Per default sind das die global gesetzten Header
     * @returns {Headers} das geänderte Headers-Objekt
     */
    setMultipleForRequest(headersToAppend: HttpHeaders, headers?: HttpHeaders) {
        let newHeaders = new HttpHeaders();
        if (headers) {
            headers.keys().forEach(key => newHeaders = newHeaders.append(key, headers.get(key)));
        }
        headersToAppend.keys().forEach(key => newHeaders = newHeaders.set(key, headersToAppend.get(key)));
        return newHeaders;
    }
}
